import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useRegisterMutation,
  useGetAreasQuery,
} from "../slices/userApiSlice";
import { setCredentials } from "../slices/authSlice";

const RegisterScreenWeb = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = location.state?.redirect || "/";

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [area, setArea] = useState("");

  const [register, { isLoading: isRegistering }] = useRegisterMutation();
  const { userInfo } = useSelector((state) => state.auth);

  const {
    data: areaData,
    isLoading: areaLoading,
    error: areaError,
  } = useGetAreasQuery();

  useEffect(() => {
    if (userInfo) {
      // Already logged in, redirect
      navigate(redirect);
    }
  }, [userInfo, navigate, redirect]);

  const validatePhone = (phone) => /^[6-9]\d{9}$/.test(phone);

  const submitHandler = async (e) => {
    e.preventDefault();

    // Basic validations
    if (
      !name ||
      !phone ||
      !password ||
      !confirmPassword ||
      !address ||
      !city ||
      !pincode ||
      !area
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    if (!validatePhone(phone)) {
      alert("Please enter a valid 10-digit Indian phone number.");
      return;
    }

    if (password !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }

    try {
      const response = await register({
        name,
        phone,
        password,
        address: {
          address,
          city,
          pincode,
          phoneNumber,
          area,
        },
      }).unwrap();

      dispatch(setCredentials(response));

      // Redirect after success
      navigate(redirect);
    } catch (err) {
      console.error("Registration error:", err);
      alert(err?.data?.message || "Something went wrong.");
    }
  };

  return (
    <Container className="my-5">
      <Row className="justify-content-md-center">
        <Col md={6}>
          <h2 className="mb-4">Register</h2>

          <Form onSubmit={submitHandler}>
            {/* Name */}
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>

            {/* Phone */}
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="10-digit phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={10}
                required
              />
            </Form.Group>

            {/* Password */}
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            {/* Confirm Password */}
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </Form.Group>

            {/* Address */}
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                placeholder="Street address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
              />
            </Form.Group>

            {/* City */}
            <Form.Group className="mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </Form.Group>

            {/* Pincode */}
            <Form.Group className="mb-3">
              <Form.Label>Pincode</Form.Label>
              <Form.Control
                type="text"
                placeholder="6-digit pincode"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                required
              />
            </Form.Group>

            {/* Contact Number (optional) */}
            <Form.Group className="mb-3">
              <Form.Label>Contact Number (Optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Contact for delivery"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </Form.Group>

            {/* Area Dropdown */}
            <Form.Group className="mb-3">
              <Form.Label>Select Area</Form.Label>
              {areaLoading ? (
                <div>
                  <Spinner animation="border" size="sm" /> Loading areas...
                </div>
              ) : areaError ? (
                <Alert variant="danger">Failed to load areas</Alert>
              ) : (
                <Form.Select
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  required
                >
                  <option value="">Select Area</option>
                  {areaData?.areas.map((areaItem) => (
                    <option key={areaItem} value={areaItem}>
                      {areaItem}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>

            {/* Submit Button */}
            <Button
              variant="primary"
              type="submit"
              disabled={isRegistering}
              className="w-100"
            >
              {isRegistering ? (
                <>
                  <Spinner as="span" animation="border" size="sm" /> Registering...
                </>
              ) : (
                "Register"
              )}
            </Button>
          </Form>

          <div className="mt-3 text-center">
            Already have an account?{" "}
            <Button
              variant="link"
              onClick={() => navigate("/", { state: { redirect } })}
            >
              Login
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default RegisterScreenWeb;
